import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import { btnQuietTextCls, btnSecondaryCls } from "components/tw/classes";
import StatusChip from "components/tw/status-chip";
import { fetchExpiringAssignedFiles } from "services/files-category.service";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { expiryStatus, useMarkExpiryHandled } from "features/cabinet-tasks/document-expiry";
import { FilterSelect, SearchBox } from "./filter-controls";
import { withThousands } from "./format-count";
import { LoadFailedNotice, QueueSkeleton } from "./queue-list-states";
import { QueueCell, QueueRow, QueueTable } from "./queue-table";
import { useQueryParams } from "./use-query-params";
import { useQueueData } from "./use-queue-data";

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 2;

/** One row of erp-backend GET /:companyId/files/expiring-assigned (all it sends of a document). */
interface ExpiringFile {
  id: string;
  name: string | null;
  title: string | null;
  dateTo: string;
  company: { id: string; name: string | null; legalName: string | null };
  customer: { id: string; name: string }[];
}

interface ExpiringAnswer {
  files: ExpiringFile[];
  pagination: { total: number };
  filterOptions: { customers: { id: string; name: string }[] };
}

/**
 * «Baigiasi galiojimas»: the documents of all the person's companies whose end date is within
 * 30 days either side of today — the same documents the «Užduotys» card counts — expired ones
 * first. A row opens its document; «Sutvarkyta» takes it off this list and the card.
 */
export default function ExpiringQueuePage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId = "" } = useParams();
  const { params, change } = useQueryParams();

  const page = Math.max(1, Math.floor(Number(params.get("page"))) || 1);
  const customer = params.get("customer") ?? "";
  const search = params.get("q") ?? "";
  // Shorter than two characters the search is not sent.
  const searchText = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : "";
  const filters = { page, pageSize: PAGE_SIZE, textSearch: searchText, customerId: customer };
  const hasFilters = Boolean(searchText || customer);

  // Marking the last rows of the last page leaves that page empty: step back to the last page that has rows.
  const stepBackFromEmptyPage = (answer: ExpiringAnswer) => {
    if (answer.files.length > 0 || page <= 1) return;
    const lastPage = Math.max(1, Math.ceil(answer.pagination.total / PAGE_SIZE));
    change({ page: lastPage > 1 ? String(lastPage) : "" });
  };

  const list = useQueueData<ExpiringAnswer>(() => fetchExpiringAssignedFiles(companyId, filters), {
    scopeKey: companyId,
    requestKey: JSON.stringify(filters),
    onAnswer: stepBackFromEmptyPage,
  });
  const markExpiryHandled = useMarkExpiryHandled(() => {
    list.reload();
    dispatch(fetchCabinetSummaryThunk(companyId));
  });

  const files = list.data?.files ?? [];
  // The number of the filtered list. A page left empty with rows still on earlier pages
  // (the step back is on its way) is not "nothing found".
  const total = list.data?.pagination.total ?? 0;
  const firstOnPage = (page - 1) * PAGE_SIZE;
  const lastOnPage = firstOnPage + files.length;
  const customerOptions = [
    { value: "", label: t("queue_filter_all") },
    ...(list.data?.filterOptions.customers ?? []).map(({ id, name }) => ({ value: id, label: name })),
  ];

  // The columns of the list, left to right; the cells of each row follow the same order.
  const columns = [
    { heading: t("queue_col_title"), width: "minmax(0,4fr)" },
    { heading: t("queue_col_company"), width: "minmax(0,2fr)" },
    { heading: t("queue_col_customer"), width: "minmax(0,2fr)" },
    { heading: t("status"), width: "11rem" },
    { heading: "", width: "13rem" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <SearchBox
          label={t("queue_search")}
          value={search}
          delayMs={SEARCH_DELAY_MS}
          onSearch={(text) => change({ q: text, page: "" })}
        />
        <FilterSelect
          label={t("queue_col_customer")}
          value={customer}
          options={customerOptions}
          onChange={(value) => change({ customer: value, page: "" })}
        />
      </div>

      {list.hasFailed && <LoadFailedNotice onRetry={list.reload} />}
      {!list.data && list.isLoading && <QueueSkeleton />}
      {list.data && total === 0 && (
        <p className="text-sm text-gray-600">{t(hasFilters ? "expiring_no_match" : "no_expiring_documents")}</p>
      )}

      {files.length > 0 && (
        <div className={list.isLoading ? "opacity-60" : ""}>
          <QueueTable label={t("tasks_group_expiring")} columns={columns} tableFrom="xl">
            {files.map((file) => {
              const documentPath = `/app/${file.company.id}/documents/view/${file.id}`;
              const status = expiryStatus(file.dateTo, t);
              return (
                <QueueRow key={file.id} onOpen={() => navigate(documentPath)}>
                  <QueueCell isTitle>
                    <Link to={documentPath} className="break-words text-indigo-600 hover:underline">
                      {file.name || file.title}
                    </Link>
                  </QueueCell>
                  <QueueCell label={t("queue_col_company")}>{file.company.name || file.company.legalName}</QueueCell>
                  <QueueCell label={t("queue_col_customer")}>
                    {file.customer.map(({ name }) => name).join(", ")}
                  </QueueCell>
                  <QueueCell>
                    <StatusChip tone={status.isExpired ? "red" : "amber"}>{status.label}</StatusChip>
                  </QueueCell>
                  <QueueCell className="flex w-full flex-wrap items-center gap-4 xl:w-auto xl:justify-end">
                    <Link to={documentPath} className={btnSecondaryCls}>
                      {t("tasks_action_open")}
                    </Link>
                    <button
                      type="button"
                      className={btnQuietTextCls}
                      onClick={() => markExpiryHandled({ fileId: file.id, companyId: file.company.id, dateTo: file.dateTo })}
                    >
                      {t("expiry_handled_action")}
                    </button>
                  </QueueCell>
                </QueueRow>
              );
            })}
          </QueueTable>

          {total > PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm text-gray-600">
              <span>
                {t("queue_page_range", {
                  from: withThousands(firstOnPage + 1),
                  to: withThousands(lastOnPage),
                  total: withThousands(total),
                })}
              </span>
              <button
                type="button"
                className={btnSecondaryCls}
                aria-label={t("queue_prev_page")}
                disabled={page <= 1}
                onClick={() => change({ page: page > 2 ? String(page - 1) : "" })}
              >
                ‹
              </button>
              <button
                type="button"
                className={btnSecondaryCls}
                aria-label={t("queue_next_page")}
                disabled={lastOnPage >= total}
                onClick={() => change({ page: String(page + 1) })}
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
